import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import { AccountTree, Block, Event, Group, HowToReg, Lock, Public, Star, Timer, InfoOutlined, ExpandMore } from '@material-ui/icons';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { AocClasses, ICharacter } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { IUser } from '@pvplevelup/core/dist/interfaces/IUser';
import {
  ITournament, ITournamentParticipant, ITournamentTeam, ParticipantRole, ParticipantStatus, TournamentStatus, TournamentType, TournamentVisibility,
} from '@pvplevelup/core/dist/interfaces/Tournament/ITournament';
import { getTeamSize, isTeamTournament } from '@pvplevelup/core/dist/tournament/TournamentRules';
import { IDungeonClearStat, IMatchView, IRoundView } from '@pvplevelup/core/dist/queries/tournament/tournament';
import { TournamentBracket, RoundTimeTarget } from './TournamentBracket';
import { TournamentStandings } from './TournamentStandings';
import { classImages } from 'utilities/ClassImages';
import { PveIcons } from 'utilities/pve_icons';
import { PageEmpty, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import {
  TYPE_COLORS, TOURNAMENT_ACCENT, bracketLabel, dungeonLabel, formatCountdown, formatDateTime, formatMinutes, getRuleLines, getStatusInfo, typeLabel,
} from './tournamentFormat';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  hero: {
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
    border: PAGE_KIT.border,
    boxShadow: PAGE_KIT.shadow,
  },
  heroShade: {
    position: 'relative',
    padding: theme.spacing(3, 3, 2.5),
  },
  kicker: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 8,
    alignItems: 'center',
    fontSize: '0.74rem',
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.72)',
  },
  heroTop: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: theme.spacing(2),
    alignItems: 'start',
  },
  title: {
    marginTop: 6,
    fontSize: '2rem',
    fontWeight: 900,
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
    textShadow: '0 2px 12px rgba(0, 0, 0, 0.5)',
    wordBreak: 'break-word',
  },
  byline: {
    marginTop: 6,
    fontSize: '0.88rem',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  status: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 7,
    alignItems: 'center',
    height: 28,
    padding: '0 12px',
    borderRadius: 14,
    fontSize: '0.78rem',
    fontWeight: 800,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
    backdropFilter: 'blur(6px)',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gridGap: theme.spacing(1.25),
    marginTop: theme.spacing(2.5),
  },
  stat: {
    display: 'grid',
    gridTemplateColumns: '36px minmax(0, 1fr)',
    gridGap: 10,
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    background: 'rgba(0, 0, 0, 0.35)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(6px)',
  },
  statIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    color: TOURNAMENT_ACCENT,
    background: 'rgba(255, 179, 0, 0.14)',
    '& svg': {
      fontSize: 19,
    },
  },
  statLabel: {
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: PAGE_KIT.faint,
  },
  statValue: {
    marginTop: 1,
    fontSize: '0.92rem',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statSub: {
    fontSize: '0.75rem',
    color: PAGE_KIT.muted,
  },
  cancelled: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: 10,
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 12,
    color: '#ffcdd2',
    background: 'rgba(198, 40, 40, 0.18)',
    border: '1px solid rgba(239, 83, 80, 0.4)',
    fontSize: '0.88rem',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 340px',
    gridGap: theme.spacing(2),
    alignItems: 'start',
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  column: {
    display: 'grid',
    gridGap: theme.spacing(2),
    minWidth: 0,
  },
  panelHead: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: 10,
    alignItems: 'center',
    padding: theme.spacing(1.5, 2.25),
    borderBottom: PAGE_KIT.divider,
  },
  panelTitle: {
    fontSize: '0.74rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.muted,
  },
  panelCount: {
    fontSize: '0.8rem',
    color: PAGE_KIT.faint,
  },
  /** tabs in a panel's header (the bracket and the final standings) */
  headTabs: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridColumnGap: 18,
    alignItems: 'center',
  },
  headTab: {
    position: 'relative',
    padding: '2px 0',
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '0.74rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.faint,
    transition: 'color 0.12s',
    '&:hover': {
      color: '#fff',
    },
  },
  headTabOn: {
    color: '#fff',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: -13,
      height: 2,
      borderRadius: 2,
      background: TOURNAMENT_ACCENT,
    },
  },
  /** a panel that opens and closes from its header */
  panelToggle: {
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.12s',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.025)',
    },
  },
  panelClosed: {
    borderBottom: 0,
  },
  toggleRight: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridColumnGap: 8,
    alignItems: 'center',
    fontSize: '0.8rem',
    color: PAGE_KIT.faint,
  },
  chevron: {
    fontSize: 20,
    color: PAGE_KIT.muted,
    transition: 'transform 0.18s',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  panelBody: {
    padding: theme.spacing(1.75, 2.25),
  },
  description: {
    fontSize: '0.92rem',
    lineHeight: 1.6,
    color: 'rgba(255, 255, 255, 0.82)',
    whiteSpace: 'pre-wrap',
  },
  rules: {
    display: 'grid',
    gridTemplateColumns: '140px minmax(0, 1fr)',
    gridRowGap: 11,
    gridColumnGap: 16,
    fontSize: '0.9rem',
  },
  ruleLabel: {
    color: PAGE_KIT.faint,
    fontWeight: 700,
  },
  ruleValue: {
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 1.45,
  },
  ruleHint: {
    display: 'block',
    marginTop: 2,
    fontSize: '0.78rem',
    color: PAGE_KIT.faint,
  },
  dungeon: {
    display: 'grid',
    gridTemplateColumns: '28px 52px minmax(0, 1fr) auto',
    gridGap: 12,
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    borderBottom: PAGE_KIT.divider,
    '&:last-child': {
      borderBottom: 0,
    },
  },
  dungeonIndex: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontSize: '0.8rem',
    fontWeight: 900,
    color: '#1a1300',
    background: `linear-gradient(135deg, #ffe082, ${TOURNAMENT_ACCENT})`,
  },
  dungeonThumb: {
    width: 52,
    height: 34,
    borderRadius: 7,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  dungeonName: {
    fontWeight: 800,
  },
  dungeonMeta: {
    fontSize: '0.78rem',
    color: PAGE_KIT.faint,
  },
  dungeonLimit: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 5,
    alignItems: 'center',
    fontSize: '0.82rem',
    fontWeight: 800,
    fontVariantNumeric: 'tabular-nums',
    '& svg': {
      fontSize: 15,
      color: TOURNAMENT_ACCENT,
    },
  },
  teams: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
    gridGap: theme.spacing(1.5),
  },
  team: {
    borderRadius: 12,
    padding: theme.spacing(1.25, 1.5),
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  teamMine: {
    borderColor: 'rgba(255, 179, 0, 0.45)',
    background: 'rgba(255, 179, 0, 0.06)',
  },
  teamHead: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridGap: 8,
    alignItems: 'center',
    marginBottom: 8,
  },
  teamName: {
    fontWeight: 800,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  teamCount: {
    fontSize: '0.75rem',
    fontWeight: 800,
    color: PAGE_KIT.faint,
    fontVariantNumeric: 'tabular-nums',
  },
  member: {
    display: 'grid',
    gridTemplateColumns: '22px minmax(0, 1fr) auto',
    gridGap: 8,
    alignItems: 'center',
    minHeight: 28,
    fontSize: '0.86rem',
  },
  memberInvited: {
    opacity: 0.55,
    fontStyle: 'italic',
  },
  avatar: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    background: 'rgba(255, 255, 255, 0.08)',
  },
  openSlot: {
    minHeight: 28,
    display: 'grid',
    alignItems: 'center',
    paddingLeft: 30,
    fontSize: '0.8rem',
    color: PAGE_KIT.faint,
  },
  participant: {
    display: 'grid',
    gridTemplateColumns: '34px minmax(0, 1fr) auto auto',
    gridGap: 12,
    alignItems: 'center',
    padding: theme.spacing(1, 2.25),
    borderBottom: PAGE_KIT.divider,
    '&:last-child': {
      borderBottom: 0,
    },
  },
  participantName: {
    fontWeight: 800,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    '&:hover': {
      color: PAGE_KIT.accent,
    },
  },
  participantMeta: {
    fontSize: '0.76rem',
    color: PAGE_KIT.faint,
  },
  classIcons: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 4,
  },
  classIcon: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'block',
  },
  tag: {
    display: 'inline-block',
    marginLeft: 6,
    padding: '0 7px',
    borderRadius: 8,
    fontSize: '0.64rem',
    fontWeight: 800,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    lineHeight: '17px',
    verticalAlign: 2,
  },
}));

const avatarOf = (user: any) => user?.avatar || undefined;

interface Props {
  tournament: ITournament;
  participants: ITournamentParticipant[];
  teams: ITournamentTeam[];
  /** average clear times, for a cup's dungeon list */
  dungeonStats?: IDungeonClearStat[];
  /** once it's running: the bracket's matches, or the cup's rounds */
  matches?: IMatchView[];
  rounds?: IRoundView[];
  /** open a match's detail */
  onMatchClick?: (match: IMatchView) => void;
  /** organizers: set a round's start time from the bracket (D28) */
  onRoundTimeClick?: (target: RoundTimeTarget) => void;
  /** panels on the right (the app: sign-up and organizer tools) */
  side?: React.ReactNode;
  /** panels on the right below the schedule (the app: organizer tools) */
  sideBottom?: React.ReactNode;
  /** organizer tools on a participant's row */
  participantActions?: (participant: ITournamentParticipant) => React.ReactNode;
  /** tools on a team card (leaders: invite / remove, organizers) */
  teamActions?: (team: ITournamentTeam) => React.ReactNode;
  /** under a team's member (leaders: remove, cancel invite) */
  memberActions?: (team: ITournamentTeam, user: IUser, invited: boolean) => React.ReactNode;
  /** where user profiles are */
  userPath?: string;
  /** buttons in the header beside the status (the app: promote in chat) */
  headerActions?: React.ReactNode;
  /** the logged in user (to mark their team) */
  myTeamId?: string | null;
}

/** A tournament's page: what it is, when, the rules, who's in, and the teams. Shared by the app and the website. */
export const TournamentView: React.FC<Props> = ({tournament: t, participants, teams, dungeonStats, matches, rounds, onMatchClick, onRoundTimeClick, side, sideBottom, participantActions, teamActions, memberActions, userPath = '/users', myTeamId, headerActions}) => {
  const classes = useStyles();
  const page = usePageStyles();
  const history = useHistory();
  const status = getStatusInfo(t.status);
  const color = TYPE_COLORS[t.type] || TOURNAMENT_ACCENT;
  const teamSize = getTeamSize(t);
  const teamType = isTeamTournament(t.type);
  const coOrganizers = (t.coOrganizers || []) as IUser[];
  const coIds = coOrganizers.map((u) => String(u._id));
  const unassigned = participants.filter((p) => !p.team && p.status == ParticipantStatus.Active);
  const firstDungeon = (t.dungeons || [])[0]?.instance;
  const banner = t.type == TournamentType.PvE && firstDungeon ? PveIcons[firstDungeon] : null;

  const characterIcons = (characters?: Array<ICharacter | any>) => (
    <span className={classes.classIcons}>
      {(characters || []).map((c: ICharacter) => (
        <Tooltip key={String(c._id)} title={`${c.name}${c.class ? ` · ${c.class}` : ''}`}>
          <img className={classes.classIcon} src={classImages.getColorIcon(c.class as AocClasses)} alt={c.class} />
        </Tooltip>
      ))}
    </span>
  );

  const stat = (icon: React.ReactNode, label: string, value: React.ReactNode, sub?: React.ReactNode) => (
    <div className={classes.stat}>
      <span className={classes.statIcon}>{icon}</span>
      <div style={{minWidth: 0}}>
        <div className={classes.statLabel}>{label}</div>
        <div className={classes.statValue}>{value}</div>
        {sub && <div className={classes.statSub}>{sub}</div>}
      </div>
    </div>
  );

  const open = t.status == TournamentStatus.SignupsOpen;
  // the rules and the participant list are long: closed until opened
  const [rulesOpen, setRulesOpen] = React.useState(false);
  // a finished tournament: the bracket (default) or the final standings, as tabs
  const [resultsTab, setResultsTab] = React.useState<'bracket' | 'standings'>('bracket');
  const [participantsOpen, setParticipantsOpen] = React.useState(false);
  const toggleHead = (title: string, isOpen: boolean, toggle: () => void, summary: React.ReactNode) => (
    <div role="button" className={clsx(classes.panelHead, classes.panelToggle, {[classes.panelClosed]: !isOpen})} onClick={toggle}>
      <span className={classes.panelTitle}>{title}</span>
      <span className={classes.toggleRight}>
        <span>{summary}</span>
        <ExpandMore className={clsx(classes.chevron, {[classes.chevronOpen]: isOpen})} />
      </span>
    </div>
  );
  const upcoming = [TournamentStatus.SignupsOpen, TournamentStatus.SignupsClosed, TournamentStatus.ReadyCheck].indexOf(t.status) != -1;

  return (
    <div>
      <div className={classes.hero} style={{background: banner
        ? `linear-gradient(100deg, rgba(14, 14, 18, 0.96) 35%, rgba(14, 14, 18, 0.72)), url(${banner}) center / cover`
        : `radial-gradient(circle at 85% 0%, ${color}38, transparent 55%), radial-gradient(circle at 0% 100%, rgba(255, 179, 0, 0.16), transparent 50%), ${PAGE_KIT.panel}`}}>
        <div className={classes.heroShade}>
          <div className={classes.heroTop}>
            <div style={{minWidth: 0}}>
              <span className={classes.kicker}>
                <span style={{color}}>{typeLabel(t.type)}</span>·<span>{bracketLabel(t.bracket)}</span>·<span>{t.server}</span>
                {t.visibility == TournamentVisibility.InviteOnly && <React.Fragment>·<span>Invite only</span></React.Fragment>}
              </span>
              <div className={classes.title}>{t.name}</div>
              <div className={classes.byline}>
                Organized by <b>{(t.organizer as IUser)?.displayname || 'unknown'}</b>
                {coOrganizers.length > 0 && <span> with {coOrganizers.map((u) => u.displayname).join(', ')}</span>}
              </div>
            </div>
            <div style={{display: 'grid', justifyItems: 'end', gridRowGap: 10}}>
              <span className={classes.status} style={{color: status.color, background: `${status.color}26`, border: `1px solid ${status.color}66`}}>
                <span className={classes.dot} style={{background: status.color, boxShadow: `0 0 8px ${status.color}`}} />{status.label}
              </span>
              {headerActions}
            </div>
          </div>
          <div className={classes.stats}>
            {stat(<Event />, 'Starts', formatDateTime(t.startAt), upcoming ? formatCountdown(t.startAt) : null)}
            {stat(<HowToReg />, 'Sign-ups close', formatDateTime(t.signupCloseAt), open ? formatCountdown(t.signupCloseAt) : 'Closed')}
            {stat(<Group />, 'Players', `${t.participantCount || 0}${t.maxParticipants ? ` / ${t.maxParticipants}` : ''}`,
              (t.participantCount || 0) < t.minParticipants ? `${t.minParticipants - (t.participantCount || 0)} more needed` : 'Enough to start')}
            {teamType && stat(<AccountTree />, 'Teams', t.teamCount || 0, t.type == TournamentType.PvE ? `Teams of ${teamSize}` : `${teamSize}v${teamSize}`)}
          </div>
          {t.status == TournamentStatus.Cancelled &&
            <div className={classes.cancelled}><Block style={{fontSize: 18}} />Cancelled: {t.cancelledReason || 'by the organizer.'}</div>
          }
        </div>
      </div>

      <div className={classes.layout}>
        <div className={classes.column}>
          {t.stage && (() => {
            const finished = t.status == TournamentStatus.Completed;
            const showing = finished ? resultsTab : 'bracket';
            const bracketTitle = t.stage == 'Cup' ? 'The cup' : t.stage == 'Shuffle' ? 'Shuffle' : t.stage == 'Groups' ? 'Groups' : 'Bracket';
            return (
              <div className={page.tablePanel}>
                <div className={classes.panelHead}>
                  {finished
                    ? <span className={classes.headTabs}>
                        <span role="button" className={clsx(classes.headTab, {[classes.headTabOn]: showing == 'bracket'})} onClick={() => setResultsTab('bracket')}>{bracketTitle}</span>
                        <span role="button" className={clsx(classes.headTab, {[classes.headTabOn]: showing == 'standings'})} onClick={() => setResultsTab('standings')}>Final standings</span>
                      </span>
                    : <span className={classes.panelTitle}>{bracketTitle}</span>}
                  <span className={classes.panelCount}>
                    {showing == 'standings'
                      ? (t.completedAt ? `Finished ${formatDateTime(t.completedAt)}` : '')
                      : (t.entryCount ? `${t.entryCount} ${teamType ? 'teams' : 'players'} · seeded by ${t.type == TournamentType.PvE ? 'role' : 'rating'}` : '')}
                  </span>
                </div>
                <div className={classes.panelBody}>
                  {showing == 'standings'
                    ? <TournamentStandings tournament={t} userPath={userPath} />
                    : <TournamentBracket tournament={t} matches={matches || []} rounds={rounds || []} onMatchClick={onMatchClick} onRoundTimeClick={onRoundTimeClick} />}
                </div>
              </div>
            );
          })()}
          {t.description &&
            <div className={page.tablePanel}>
              <div className={classes.panelHead}><span className={classes.panelTitle}>About</span><span /></div>
              <div className={clsx(classes.panelBody, classes.description)}>{t.description}</div>
            </div>
          }

          <div className={page.tablePanel}>
            {toggleHead('Rules', rulesOpen, () => setRulesOpen(!rulesOpen), rulesOpen ? 'Hide' : `${typeLabel(t.type)} · ${bracketLabel(t.bracket)} · show all`)}
            {rulesOpen &&
              <div className={clsx(classes.panelBody, classes.rules)}>
                {getRuleLines(t).map((line) => (
                  <React.Fragment key={line.label}>
                    <span className={classes.ruleLabel}>{line.label}</span>
                    <span className={classes.ruleValue}>{line.value}{line.hint && <span className={classes.ruleHint}>{line.hint}</span>}</span>
                  </React.Fragment>
                ))}
              </div>
            }
          </div>

          {t.type == TournamentType.PvE && (t.dungeons || []).length > 0 &&
            <div className={page.tablePanel}>
              <div className={classes.panelHead}>
                <span className={classes.panelTitle}>The cup</span>
                <span className={classes.panelCount}>Everyone finishes a dungeon before the next one starts</span>
              </div>
              <div className={classes.panelBody} style={{paddingTop: 4, paddingBottom: 4}}>
                {(t.dungeons || []).map((d, i) => {
                  const stats = (dungeonStats || []).find((s) => s.instance == d.instance);
                  return (
                    <div key={`${d.instance}-${i}`} className={classes.dungeon}>
                      <span className={classes.dungeonIndex}>{i + 1}</span>
                      <span className={classes.dungeonThumb} style={PveIcons[d.instance] ? {backgroundImage: `url(${PveIcons[d.instance]})`} : undefined} />
                      <div style={{minWidth: 0}}>
                        <div className={classes.dungeonName}>{dungeonLabel(d.instance)}</div>
                        <div className={classes.dungeonMeta}>
                          {stats?.runs ? `Average clear ${formatMinutes(stats.averageClearTime)} · fastest ${formatMinutes(stats.fastestClearTime)}` : 'No recorded clears yet'}
                        </div>
                      </div>
                      <Tooltip title="Time limit: runs still going are stopped and count as not cleared">
                        <span className={classes.dungeonLimit}><Timer />{d.timeLimitMinutes} min</span>
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
            </div>
          }

          {teamType &&
            <div className={page.tablePanel}>
              <div className={classes.panelHead}>
                <span className={classes.panelTitle}>Teams</span>
                <span className={classes.panelCount}>{teams.length} {teams.length == 1 ? 'team' : 'teams'}</span>
              </div>
              <div className={classes.panelBody}>
                {teams.length > 0
                  ? <div className={classes.teams}>
                      {teams.map((team) => {
                        const members = (team.members || []) as IUser[];
                        const invites = (team.invites || []) as IUser[];
                        const openSlots = Math.max(0, teamSize - members.length - invites.length);
                        return (
                          <div key={String(team._id)} className={clsx(classes.team, {[classes.teamMine]: myTeamId != null && String(team._id) == myTeamId})}>
                            <div className={classes.teamHead}>
                              <span className={classes.teamName} title={team.name}>{team.name}</span>
                              <span className={classes.teamCount}>{members.length}/{teamSize}</span>
                            </div>
                            {members.map((user) => {
                              const isLeader = String((team.leader as IUser)?._id) == String(user._id);
                              return (
                                <div key={String(user._id)} className={classes.member}>
                                  {avatarOf(user) ? <img className={classes.avatar} src={avatarOf(user)} alt="" /> : <span className={classes.avatar} />}
                                  <span style={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                    {user.displayname}
                                    {isLeader && <Tooltip title="Team leader"><Star style={{fontSize: 13, color: TOURNAMENT_ACCENT, marginLeft: 4, verticalAlign: -2}} /></Tooltip>}
                                  </span>
                                  {memberActions && !isLeader ? memberActions(team, user, false) : <span />}
                                </div>
                              );
                            })}
                            {invites.map((user) => (
                              <div key={`invite-${user._id}`} className={clsx(classes.member, classes.memberInvited)}>
                                {avatarOf(user) ? <img className={classes.avatar} src={avatarOf(user)} alt="" /> : <span className={classes.avatar} />}
                                <span>{user.displayname} · invited</span>
                                {memberActions ? memberActions(team, user, true) : <span />}
                              </div>
                            ))}
                            {Array.from({length: openSlots}).map((_, i) => <div key={i} className={classes.openSlot}>Open slot</div>)}
                            {teamActions && teamActions(team)}
                          </div>
                        );
                      })}
                    </div>
                  : <div style={{color: PAGE_KIT.faint, fontSize: '0.88rem'}}>
                      {t.teamFormation == 'Automatic' ? 'The server makes the teams when the tournament starts.' : t.teamFormation == 'CaptainsDraft' ? 'The captains draft the teams once the ready check is over.' : 'No teams yet. Sign up as a team leader to start one.'}
                    </div>
                }
                {unassigned.length > 0 && t.status != TournamentStatus.Running && t.status != TournamentStatus.Completed &&
                  <div style={{marginTop: 14, fontSize: '0.82rem', color: PAGE_KIT.faint, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gridGap: 8}}>
                    <InfoOutlined style={{fontSize: 16}} />
                    <span>
                      {unassigned.length} {unassigned.length == 1 ? 'player is' : 'players are'} not on a team yet. At the start they fill teams that need players;
                      the rest are formed into teams by matchmaking, and anyone left over becomes a substitute.
                    </span>
                  </div>
                }
              </div>
            </div>
          }

          <div className={page.tablePanel}>
            {toggleHead('Participants', participantsOpen, () => setParticipantsOpen(!participantsOpen), `${participants.length} signed up`)}
            {participantsOpen && (participants.length > 0
              ? participants.map((p) => {
                  const user = p.user as IUser;
                  const team = teams.find((tm) => String(tm._id) == String(p.team));
                  const isCo = coIds.indexOf(String(user?._id)) != -1;
                  return (
                    <div key={String(p._id)} className={classes.participant}>
                      {avatarOf(user) ? <img className={classes.avatar} style={{width: 34, height: 34}} src={avatarOf(user)} alt="" /> : <span className={classes.avatar} style={{width: 34, height: 34}} />}
                      <div style={{minWidth: 0}}>
                        <div className={classes.participantName} onClick={() => user?._id && history.push(`${userPath}/${user._id}`)}>
                          {user?.displayname || 'Unknown'}
                          {p.role == ParticipantRole.Leader && <span className={classes.tag} style={{color: TOURNAMENT_ACCENT, background: 'rgba(255, 179, 0, 0.14)'}}>Leader</span>}
                          {p.role == ParticipantRole.Captain && <span className={classes.tag} style={{color: TOURNAMENT_ACCENT, background: 'rgba(255, 179, 0, 0.14)'}}>Captain</span>}
                          {isCo && <span className={classes.tag} style={{color: PAGE_KIT.accent, background: 'rgba(66, 165, 245, 0.14)'}}>Co-organizer</span>}
                          {p.status == ParticipantStatus.Substitute && <span className={classes.tag} style={{color: '#b0bec5', background: 'rgba(176, 190, 197, 0.14)'}}>Substitute</span>}
                          {p.readyAt && <span className={classes.tag} style={{color: '#81c784', background: 'rgba(102, 187, 106, 0.14)'}}>Ready</span>}
                        </div>
                        <div className={classes.participantMeta}>
                          {teamType ? (team ? team.name : 'Not on a team yet') : 'Duelist'}
                          {p.lateJoin ? ' · joined late' : ''}
                        </div>
                      </div>
                      {characterIcons(p.characters as ICharacter[])}
                      {participantActions ? participantActions(p) : <span />}
                    </div>
                  );
                })
              : <PageEmpty icon={<Group />} title="Nobody has signed up yet" text={open ? 'Be the first: sign-ups are open.' : undefined} />)
            }
          </div>
        </div>

        <div className={classes.column}>
          {side}
          <div className={page.tablePanel}>
            <div className={classes.panelHead}><span className={classes.panelTitle}>Schedule</span><span /></div>
            <div className={clsx(classes.panelBody, classes.rules)} style={{gridTemplateColumns: '110px minmax(0, 1fr)'}}>
              <span className={classes.ruleLabel}>Sign-ups</span><span className={classes.ruleValue}>Until {formatDateTime(t.signupCloseAt)}</span>
              <span className={classes.ruleLabel}>Ready check</span>
              <span className={classes.ruleValue}>
                From {formatDateTime(new Date(new Date(t.startAt).getTime() - t.readyWindowMinutes * 60000).toISOString())}
                <span className={classes.ruleHint}>Ready up by {formatDateTime(new Date(new Date(t.startAt).getTime() + t.graceMinutes * 60000).toISOString())} or you'll miss the start.</span>
              </span>
              <span className={classes.ruleLabel}>Start</span><span className={classes.ruleValue}>{formatDateTime(t.startAt)}</span>
              <span className={classes.ruleLabel}>Season</span><span className={classes.ruleValue}>{t.season ? `Season ${t.season}` : 'Current season'}</span>
              <span className={classes.ruleLabel}>Server</span><span className={classes.ruleValue}><Public style={{fontSize: 14, verticalAlign: -2, marginRight: 4}} />{t.server}</span>
              {t.visibility == TournamentVisibility.InviteOnly && <React.Fragment><span className={classes.ruleLabel}>Entry</span><span className={classes.ruleValue}><Lock style={{fontSize: 14, verticalAlign: -2, marginRight: 4}} />Invite only</span></React.Fragment>}
            </div>
          </div>
          {sideBottom}
        </div>
      </div>
    </div>
  );
}
